import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import graspdLogo from '../assets/graspd-logo.svg';
import '../styles/splash.css';
import '../styles/legal.css';

const TermsPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleBack = () => {
        // Go back if there's history, otherwise go to home
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="splash-page-root legal-page-root">
            <SEO
                title="Terms of Service"
                description="Read the Terms of Service for Graspd, the AI-powered learning platform. Understand your rights and responsibilities when using our service."
                path="/terms"
            />

            {/* Minimal Header */}
            <header className="legal-minimal-header">
                <button
                    onClick={handleBack}
                    className="legal-back-btn"
                    aria-label="Go back"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" />
                        <path d="M12 19l-7-7 7-7" />
                    </svg>
                    <span>Back</span>
                </button>
                <Link to="/" className="legal-logo-link" aria-label="Go to homepage">
                    <img src={graspdLogo} alt="Graspd" className="legal-logo" />
                </Link>
                <div className="legal-header-spacer" />
            </header>

            <main className="legal-page">
                <div className="legal-container">
                    <header className="legal-header">
                        <h1>Terms of Service</h1>
                        <p className="legal-updated">Last updated: January 2025</p>
                    </header>

                    <div className="legal-content">
                        <section>
                            <h2>1. Acceptance of Terms</h2>
                            <p>
                                By accessing or using Graspd ("the Service"), operated by Graspd, LLC, an Arizona
                                limited liability company, you agree to be bound by these Terms of Service and our{' '}
                                <Link to="/privacy">Privacy Policy</Link>. If you do not agree to these terms, do not
                                use our Service. We reserve the right to update these terms at any time, and your
                                continued use of the Service constitutes acceptance of any changes.
                            </p>
                        </section>

                        <section>
                            <h2>2. Description of Service</h2>
                            <p>
                                Graspd is an AI-powered learning platform that provides personalized educational content
                                and courses. The Service generates learning materials tailored to your goals, tracks your
                                progress through structured learning paths, and creates comprehensive learner profiles
                                based on your usage.
                            </p>
                        </section>

                        <section>
                            <h2>3. Account Registration</h2>
                            <p>To use certain features of the Service, you must create an account. You agree to:</p>
                            <ul>
                                <li>Provide accurate and complete information during registration</li>
                                <li>Maintain the security of your account credentials</li>
                                <li>Notify us immediately of any unauthorized access</li>
                                <li>Be responsible for all activities under your account</li>
                            </ul>
                        </section>

                        <section>
                            <h2>4. Data Ownership and License Grant</h2>
                            <p>
                                <strong>Graspd's Ownership:</strong> You acknowledge and agree that all data generated
                                through your use of the Service, including but not limited to your learning profile,
                                usage patterns, assessment results, progress data, interaction data, and any insights
                                or analytics derived therefrom (collectively, "User Data"), are owned exclusively by
                                Graspd. User Data constitutes valuable proprietary business assets of Graspd.
                            </p>
                            <p>
                                <strong>Your License to Graspd:</strong> By using the Service, you grant Graspd a
                                perpetual, irrevocable, worldwide, royalty-free, fully transferable, and sublicensable
                                license to use, reproduce, modify, adapt, publish, translate, create derivative works
                                from, distribute, perform, display, and otherwise exploit any content you provide or
                                generate through the Service, including content extracted from uploaded documents,
                                course requests, and any other user-generated content, for any purpose whatsoever.
                            </p>
                            <p>
                                <strong>Permitted Uses:</strong> Without limiting the foregoing, Graspd may use User
                                Data and user-generated content for: improving and developing the Service; training AI
                                and machine learning models; research and analytics; creating and licensing aggregated
                                or anonymized data products; marketing and promotional purposes; and any other lawful
                                business purpose.
                            </p>
                        </section>

                        <section>
                            <h2>5. Subscription and Payments</h2>
                            <p>
                                <strong>Billing:</strong> Subscriptions are billed in advance on a monthly or annual
                                basis. Payment is processed securely through Stripe.
                            </p>
                            <p>
                                <strong>Cancellation:</strong> You may cancel your subscription at any time through your
                                account settings. Cancellation takes effect at the end of your current billing period.
                                Cancellation does not affect Graspd's ownership of or rights to User Data.
                            </p>
                            <p>
                                <strong>Refunds:</strong> We do not offer refunds for partial subscription periods. If
                                you cancel, you will retain access until the end of your paid period.
                            </p>
                        </section>

                        <section>
                            <h2>6. Acceptable Use</h2>
                            <p>You agree not to:</p>
                            <ul>
                                <li>Use the Service for any unlawful purpose</li>
                                <li>Attempt to gain unauthorized access to our systems</li>
                                <li>Interfere with or disrupt the Service</li>
                                <li>Share your account with others or allow multiple users on a single account</li>
                                <li>Reproduce, distribute, or resell any content from the Service</li>
                                <li>Use automated means to access the Service without permission</li>
                                <li>Upload malicious content, viruses, or harmful code</li>
                                <li>Attempt to reverse engineer, decompile, or extract our AI models or algorithms</li>
                            </ul>
                        </section>

                        <section>
                            <h2>7. Intellectual Property</h2>
                            <p>
                                The Service and its original content, features, functionality, AI models, algorithms,
                                and underlying technology are owned by Graspd and are protected by copyright, trademark,
                                trade secret, and other intellectual property laws.
                            </p>
                            <p>
                                Content generated by our AI for your learning is licensed to you for personal,
                                non-commercial educational use only during your active subscription. You may not
                                redistribute, sell, or republish this content. This license terminates upon cancellation
                                of your subscription or termination of your account.
                            </p>
                        </section>

                        <section>
                            <h2>8. AI-Generated Content</h2>
                            <p>
                                Our Service uses artificial intelligence to generate educational content. While we
                                strive for accuracy:
                            </p>
                            <ul>
                                <li>AI-generated content may occasionally contain errors or inaccuracies</li>
                                <li>Content should be used as a learning aid, not as professional advice</li>
                                <li>We recommend verifying critical information through additional sources</li>
                                <li>Graspd owns all AI-generated content and may use it for any purpose</li>
                            </ul>
                        </section>

                        <section>
                            <h2>9. Data Retention After Termination</h2>
                            <p>
                                Upon termination or cancellation of your account, Graspd retains the right to keep, use,
                                and manage all User Data, aggregated data, anonymized data, and any derived insights or
                                analytics indefinitely. This data remains the property of Graspd and may be used for any
                                lawful purpose, including but not limited to AI training, research, product development,
                                and licensing to third parties.
                            </p>
                        </section>

                        <section>
                            <h2>10. Disclaimer of Warranties</h2>
                            <p className="legal-caps">
                                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
                                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT
                                GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. YOUR USE OF
                                THE SERVICE IS AT YOUR OWN RISK.
                            </p>
                        </section>

                        <section>
                            <h2>11. Limitation of Liability</h2>
                            <p className="legal-caps">
                                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, GRASPD AND ITS OFFICERS, DIRECTORS,
                                EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                                CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUES, DATA, USE,
                                GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM: (A) YOUR USE OF OR INABILITY
                                TO USE THE SERVICE; (B) ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SERVERS OR ANY
                                PERSONAL INFORMATION STORED THEREIN; (C) ANY ERRORS OR INACCURACIES IN AI-GENERATED
                                CONTENT; OR (D) ANY OTHER MATTER RELATING TO THE SERVICE. IN NO EVENT SHALL GRASPD'S
                                TOTAL LIABILITY EXCEED THE AMOUNTS PAID BY YOU TO GRASPD IN THE TWELVE (12) MONTHS
                                PRECEDING THE CLAIM.
                            </p>
                        </section>

                        <section>
                            <h2>12. Indemnification</h2>
                            <p>
                                You agree to indemnify, defend, and hold harmless Graspd and its officers, directors,
                                employees, agents, and affiliates from and against any and all claims, damages, losses,
                                costs, and expenses (including reasonable attorneys' fees) arising from or relating to
                                your use of the Service, your violation of these Terms, or your violation of any rights
                                of another party.
                            </p>
                        </section>

                        <section>
                            <h2>13. Termination</h2>
                            <p>
                                We reserve the right to suspend or terminate your account and access to the Service at
                                our sole discretion, without notice, for conduct that we believe violates these Terms or
                                is harmful to other users, us, or third parties, or for any other reason. Upon
                                termination, your right to use the Service ceases immediately, but Graspd's ownership
                                of and rights to User Data survive.
                            </p>
                        </section>

                        <section>
                            <h2>14. Governing Law and Dispute Resolution</h2>
                            <p>
                                These Terms shall be governed by and construed in accordance with the laws of the State
                                of Arizona and applicable federal laws of the United States, without regard to conflict
                                of law principles. Any legal action or proceeding arising out of or relating to these
                                Terms shall be brought exclusively in the state or federal courts located in Maricopa
                                County, Arizona, and you consent to the personal jurisdiction of such courts.
                            </p>
                        </section>

                        <section>
                            <h2>15. Severability</h2>
                            <p>
                                If any provision of these Terms is found to be unenforceable or invalid, that provision
                                shall be limited or eliminated to the minimum extent necessary, and the remaining
                                provisions shall remain in full force and effect.
                            </p>
                        </section>

                        <section>
                            <h2>16. Entire Agreement</h2>
                            <p>
                                These Terms, together with the <Link to="/privacy">Privacy Policy</Link>, constitute
                                the entire agreement between you and Graspd regarding the Service and supersede all
                                prior agreements and understandings.
                            </p>
                        </section>

                        <section>
                            <h2>17. Contact Us</h2>
                            <p>
                                If you have any questions about these Terms of Service, please contact us at{' '}
                                <a href="mailto:contact@graspd.ai">contact@graspd.ai</a>.
                            </p>
                        </section>

                        <div className="legal-cross-link">
                            <p>
                                Please also review our <Link to="/privacy">Privacy Policy</Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default TermsPage;
